import { useMemo } from 'react';
import { formatToDate } from 'utils/formatToDate';

import TableCellWithPublicationStatus from 'components/TableCellWithPublicationStatus';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import useCertificateHelper from 'hooks/useCertificateHelper';
import { ADDITIONAL_DETAILS, NESTED_DETAILS } from 'models/certificate.model';
import { COMPANY } from 'models/company.model';
import { CATEGORY_TYPE } from 'models/documents.model';

import {
  AuthoritiesPageTabsEnum,
  BatteryCellsPageTabsEnum,
  BatteryPacksPageTabsEnum,
  ChemistryPageTabsEnum,
} from '../../constants';

const useConfig = () => {
  const { currentUser } = useAuthenticatedContext();
  const { getLabel } = useCertificateHelper();

  const config = useMemo<any>(() => {
    switch (currentUser?.company) {
      case COMPANY.CHEMISTRY:
      default:
        return {
          categoryByTabs: {
            [ChemistryPageTabsEnum.TEST_RESULTS]: CATEGORY_TYPE.CHEMISTRY_TEST_RESULTS,
          },
          configByTabs: {
            [ChemistryPageTabsEnum.TEST_RESULTS]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.RECIPIENT),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.RECIPIENT],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME],
              },
              {
                title: getLabel(NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.TEST_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.TEST_DATE],
                render: formatToDate,
              },
            ],
          },
        };
      case COMPANY.BATTERY_CELLS:
        return {
          categoryByTabs: {
            [BatteryCellsPageTabsEnum.TEST_RESULTS]: CATEGORY_TYPE.BATTERY_CELLS_TEST_RESULTS,
          },
          configByTabs: {
            [BatteryCellsPageTabsEnum.TEST_RESULTS]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.RECIPIENT),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.RECIPIENT],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELL_MODEL],
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.TEST_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.TEST_DATE],
                render: formatToDate,
              },
            ],
          },
        };
      case COMPANY.BATTERY_PACKS:
        return {
          categoryByTabs: {
            [BatteryPacksPageTabsEnum.TEST_RESULTS]: CATEGORY_TYPE.BATTERY_PACKS_TEST_RESULTS,
          },
          configByTabs: {
            [BatteryPacksPageTabsEnum.TEST_RESULTS]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.RECIPIENT),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.RECIPIENT],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(NESTED_DETAILS.EVMODEL),
                dataIndex: ['additionalDetails', NESTED_DETAILS.EVMODEL],
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.TEST_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.TEST_DATE],
                render: formatToDate,
              },
            ],
          },
        };
      case COMPANY.AUTHORITIES:
        return {
          categoryByTabs: {
            [AuthoritiesPageTabsEnum.CHEMISTRY]: CATEGORY_TYPE.CHEMISTRY_TEST_RESULTS,
            [AuthoritiesPageTabsEnum.BATTERY_CELLS]: CATEGORY_TYPE.BATTERY_CELLS_TEST_RESULTS,
            [AuthoritiesPageTabsEnum.BATTERY_PACKS]: CATEGORY_TYPE.BATTERY_PACKS_TEST_RESULTS,
          },
          configByTabs: {
            [AuthoritiesPageTabsEnum.CHEMISTRY]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.ISSUER),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.ISSUER],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME],
              },
              {
                title: getLabel(NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.TEST_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.TEST_DATE],
                render: formatToDate,
              },
            ],
            [AuthoritiesPageTabsEnum.BATTERY_CELLS]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.ISSUER),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.ISSUER],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELL_MODEL],
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.TEST_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.TEST_DATE],
                render: formatToDate,
              },
            ],
            [AuthoritiesPageTabsEnum.BATTERY_PACKS]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.ISSUER),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.ISSUER],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(NESTED_DETAILS.EVMODEL),
                dataIndex: ['additionalDetails', NESTED_DETAILS.EVMODEL],
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.TEST_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.TEST_DATE],
                render: formatToDate,
              },
            ],
          },
        };
    }
  }, [currentUser?.company, getLabel]);

  return config;
};

export default useConfig;
